import { makeAddNewCallback } from "../../redux/slices/addNew.ts";
import {
  CompanyCreate,
  DriverCreate,
  ProductCreate,
  VehicleCreate,
} from "../../api/index.ts";
import { configProps } from "../../types/index.ts";
import { Forwarding } from "../../types/forwarding.ts";
import DeliveryStatusTag from "../../components/DeliveryStatusTag.tsx";
import WaybillButton from "../../components/WaybillButton.tsx";
import { getCountryName } from "../../helpers/countryNames.ts";

export const forwardingOrdersConfig: configProps<"forwardingOrders"> = {
  view: "forwardingOrders",
  title: "Spedycja",
  allowMultipleNewEntries: true,
  columns: [
    {
      id: 1,
      field: "date",
      header: "Data",
      formGroup: "origin",
      isDefaultVisible: true,
      className: "column--extra-small",
      inputType: "calendar",
      required: true,
      isDefaultSort: true,
      sortOrder: -1 as const,
    },
    {
      id: 2,
      field: "buyer",
      header: "Płatnik",
      formGroup: "origin",
      isDefaultVisible: true,
      className: "column--flexible",
      inputType: "dropdown",
      optionsKey: "buyers",
      allowAddNew: makeAddNewCallback({
        field: "buyer",
        type: "company",
        makeInitialValues: ({ initialValueString: name }) =>
          ({
            name,
            is_buyer: true,
            is_transporter: false,
            is_supplier: false,
          }) satisfies Partial<CompanyCreate>,
      }),
      enableFilter: true,
      required: true,
    },
    {
      id: 3,
      field: "clientRate",
      header: "Stawka klienta",
      formGroup: "origin",
      isDefaultVisible: true,
      className: "column--extra-small",
      inputType: "number",
      required: true,
    },
    {
      id: 4,
      field: "salesInvoice",
      header: "Faktura Sprz.",
      formGroup: "none",
      isDefaultVisible: false,
      className: "column--extra-small",
      inputType: "text",
    },
    {
      id: 5,
      field: "salesOrderNumber",
      header: "N. zamówienia sprz.",
      formGroup: "origin",
      isDefaultVisible: false,
      className: "column--extra-small",
      inputType: "text",
    },
    {
      id: 6,
      field: "deliveryStatus",
      header: "Status dostawy",
      formGroup: "none",
      isDefaultVisible: true,
      className: "column--small",
      inputType: "dropdown",
      optionsKey: "deliveryStatus",
      editable: false,
      enableFilter: true,
      body: (rowData: Forwarding) => (
        <div className="status-tag-wrapper">
          <DeliveryStatusTag deliveryStatus={rowData.deliveryStatus} />
          <WaybillButton
            deliveryStatus={rowData.deliveryStatus}
            rowId={rowData.id}
          />
        </div>
      ),
    },
    {
      id: 7,
      field: "weight",
      header: "Waga",
      formGroup: "none",
      isDefaultVisible: true,
      className: "column--extra-small",
      inputType: "number",
    },
    {
      id: 8,
      field: "product",
      header: "Towar",
      formGroup: "origin",
      isDefaultVisible: true,
      className: "column--small",
      inputType: "dropdown",
      optionsKey: "products",
      allowAddNew: makeAddNewCallback({
        field: "product",
        type: "product",
        makeInitialValues: ({ initialValueString: name }) =>
          ({
            name,
          }) satisfies Partial<ProductCreate>,
      }),
      enableFilter: true,
      required: true,
    },
    {
      id: 9,
      field: "carrier",
      header: "Przewożnik",
      formGroup: "delivery",
      isDefaultVisible: true,
      className: "column--small",
      inputType: "dropdown",
      optionsKey: "transporters",
      enableFilter: true,
      allowAddNew: makeAddNewCallback({
        field: "carrier",
        type: "company",
        makeInitialValues: ({ initialValueString: name }) =>
          ({
            name,
            is_buyer: false,
            is_transporter: true,
            is_supplier: false,
          }) satisfies Partial<CompanyCreate>,
      }),
    },

    {
      id: 10,
      field: "transportRate",
      header: "Stawka Tr.",
      formGroup: "delivery",
      isDefaultVisible: false,
      className: "column--small",
      inputType: "number",
    },
    {
      id: 11,
      field: "purchaseInvoice",
      header: "Faktura Zak.",
      formGroup: "none",
      isDefaultVisible: false,
      className: "column--small",
      inputType: "text",
    },
    {
      id: 12,
      field: "purchaseOrderNumber",
      header: "N. zamówienia zak.",
      formGroup: "destination",
      isDefaultVisible: false,
      className: "column--extra-small",
      inputType: "text",
    },
    {
      id: 13,
      field: "driver",
      header: "Kierowca",
      formGroup: "delivery",
      isDefaultVisible: true,
      className: "column--small",
      inputType: "dropdown",
      optionsKey: "drivers",
      allowAddNew: makeAddNewCallback({
        field: "driver",
        type: "driver",
        makeInitialValues: ({ initialValueString: name }) =>
          ({
            last_name: name,
          }) satisfies Partial<DriverCreate>,
      }),
      enableFilter: true,
    },
    {
      id: 14,
      field: "driverManager",
      header: "Kierownik",
      formGroup: "none",
      isDefaultVisible: true,
      className: "column--flexible",
      editable: false,
      optionsKey: "organizationalUsers",
      enableFilter: true,
      body: (rowData) => rowData.driverManager ?? "",
    },
    {
      id: 16,
      field: "trucks",
      header: "Ciezarowki",
      formGroup: "delivery",
      isDefaultVisible: true,
      className: "column--small",
      inputType: "dropdown",
      contextInputType: {
        driverTable: "chips",
        driverDialog: "dropdown",
        deliveryDialog: "dropdown",
        deliveryTable: "dropdown",
      },
      optionsKey: "trucks",
      allowAddNew: makeAddNewCallback({
        field: "trucks",
        type: "vehicle",
        makeInitialValues: ({
          initialValueString: number,
          row,
        }: {
          initialValueString: string;
          row: { driverId: number | null };
        }) =>
          ({
            number,
            type: "truck",
            ...(row.driverId ? { driver_ids: [row.driverId] } : {}),
          }) satisfies Partial<VehicleCreate>,
      }),
    },
    {
      id: 17,
      field: "trailers",
      header: "Przyczepy",
      formGroup: "delivery",
      isDefaultVisible: true,
      className: "column--small",
      inputType: "dropdown",
      contextInputType: {
        driverTable: "chips",
        driverDialog: "dropdown",
        deliveryDialog: "dropdown",
        deliveryTable: "dropdown",
      },
      optionsKey: "trailers",
      allowAddNew: makeAddNewCallback({
        field: "trailers",
        type: "vehicle",
        makeInitialValues: ({
          initialValueString: number,
          row,
        }: {
          initialValueString: string;
          row: { driverId: number | null };
        }) =>
          ({
            number,
            type: "trailer",
            ...(row.driverId ? { driver_ids: [row.driverId] } : {}),
          }) satisfies Partial<VehicleCreate>,
      }),
    },
    {
      id: 18,
      field: "loadingLocation",
      header: "Miejsce zał.",
      formGroup: "origin",
      isDefaultVisible: true,
      className: "column--medium",
      required: true,
      inputType: "dropdown",
      optionsKey: "locations",
      allowAddNew: makeAddNewCallback({
        field: "loadingLocation",
        type: "address",
        makeInitialValues: ({ initialValueString: address }) => ({ address }),
      }),
    },
    {
      id: 19,
      field: "unloadingLocation",
      header: "Miejsce rozł.",
      formGroup: "destination",
      isDefaultVisible: true,
      className: "column--medium",
      inputType: "dropdown",
      optionsKey: "locations",
      allowAddNew: makeAddNewCallback({
        field: "unloadingLocation",
        type: "address",
        makeInitialValues: ({ initialValueString: address }) => ({ address }),
      }),
    },
    {
      id: 20,
      field: "loadingCity",
      header: "Miasto zał.",
      formGroup: "origin",
      isDefaultVisible: true,
      className: "column--medium",
      editable: false,
      optionsKey: "cities",
      enableFilter: true,
    },
    {
      id: 21,
      field: "loadingCountry",
      header: "Kraj zał.",
      formGroup: "origin",
      isDefaultVisible: true,
      className: "column--medium",
      editable: false,
      body: (rowData) => getCountryName(rowData.loadingCountry),
    },
    {
      id: 22,
      field: "unloadingCity",
      header: "Miasto rozł.",
      formGroup: "destination",
      isDefaultVisible: true,
      className: "column--medium",
      editable: false,
      optionsKey: "cities",
      enableFilter: true,
    },
    {
      id: 23,
      field: "unloadingCountry",
      header: "Kraj rozł.",
      formGroup: "destination",
      isDefaultVisible: true,
      className: "column--medium",
      editable: false,
      body: (rowData) => getCountryName(rowData.unloadingCountry),
    },
    {
      id: 24,
      field: "profit",
      header: "Zysk",
      formGroup: "none",
      headerIcon: "calculator",
      headerIconTitle: "Pole wyliczane automatycznie. Nie można edytować.",
      isDefaultVisible: false,
      className: "column--extra-small",
      inputType: "number",
      editable: false,
    },
    {
      id: 25,
      field: "number",
      header: "Numer",
      formGroup: "none",
      isDefaultVisible: false,
      className: "column--extra-small",
      inputType: "text",
      editable: false,
    },
  ],
  fieldNameMap: {
    date: "date",
    client_rate: "clientRate",
    sales_invoice: "salesInvoice",
    weight: "weight",
    transport_rate: "transportRate",
    purchase_invoice: "purchaseInvoice",
    purchase_order_number: "purchaseOrderNumber",
    sales_order_number: "salesOrderNumber",
    client_id: "buyer",
    status: "deliveryStatus",
    marked_fields: "marked",
    product_id: "product",
    transporter_id: "carrier",
    loading_location_id: "loadingLocation",
    unloading_location_id: "unloadingLocation",
    driver_id: "driver",
    truck_id: "trucks",
    trailer_id: "trailers",
  },
};
